import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageHeader, Panel, Button, Field, FormRow, Textarea, Select, DataTable, Chip, Modal, Tabs, Switch, useToast, type Column, type Tab } from '../ds/index'
import { useSession, isAdmin } from '../lib/session'
import AccountSettings from './settings/Account'
import EmailSettings from './settings/Emails'
import Connections from './settings/Connections'
import MyGroups from './settings/MyGroups'
import PricingPage from './Pricing'
import Resources from './Resources'
import { useApi } from '../lib/useApi'
import { post, patch } from '../lib/api'
import type { Org, Category, Area, Question } from '../lib/types'
import { LoadingBlock, ErrorBlock } from '../components/Loading'
import { WEIGHTS } from '../components/ScoreChip'

export default function Settings() {
  const { me } = useSession()
  const [params, setParams] = useSearchParams()
  const admin = isAdmin(me)
  const rep = me?.user.role === 'rep'
  const tabs: Tab<string>[] = [
    ...(rep ? [{ value: 'groups', label: 'Your groups' }] : []),
    { value: 'account', label: 'Your account' },
    ...(admin ? [{ value: 'company', label: 'Company' }, { value: 'pricing', label: 'Tiers and pricing' }, { value: 'resources', label: 'Client resources' }, { value: 'emails', label: 'Client emails' }, { value: 'connections', label: 'Connections' }] : []),
  ]
  const want = params.get('tab') || (admin ? 'company' : rep ? 'groups' : 'account')
  const tab = tabs.some((t) => t.value === want) ? want : 'account'
  return (
    <>
      <PageHeader title="Settings" subtitle={admin ? 'Your own login, how this company works, what subscribers receive, and what the platform is connected to.' : rep ? 'The Facebook groups you work, and your name, phone and password.' : 'Your name, phone and password.'} />
      <div style={{ marginBottom: 'var(--s-6)' }}><Tabs tabs={tabs} value={tab} onChange={(v) => setParams({ tab: v }, { replace: true })} label="Settings sections" /></div>
      {tab === 'groups' && <MyGroups />}
      {tab === 'account' && <AccountSettings />}
      {tab === 'company' && <CompanySettings />}
      {tab === 'emails' && <EmailSettings />}
      {tab === 'connections' && <Connections />}
      {tab === 'pricing' && <PricingPage embedded />}
      {tab === 'resources' && <Resources embedded />}
    </>
  )
}

const hourLabel = (h: number) => h === 0 || h === 24 ? 'midnight' : h === 12 ? 'noon' : h < 12 ? `${h}am` : `${h - 12}pm`

function CompanySettings() {
  const org = useApi<{ org: Org }>('/api/settings')
  const cats = useApi<{ categories: Category[] }>('/api/categories')
  const areas = useApi<{ areas: Area[] }>('/api/areas')
  const toast = useToast()
  const [f, setF] = useState<Org | null>(null)
  const [qs, setQs] = useState<Question[]>([])
  const [editCat, setEditCat] = useState<Category | 'new' | null>(null)
  const [editArea, setEditArea] = useState<Area | 'new' | null>(null)

  useEffect(() => { if (org.data) { setF(org.data.org); setQs(org.data.org.lead_questions) } }, [org.data])

  if (org.error) return <ErrorBlock error={org.error} />
  if (!f) return <LoadingBlock />

  const saveOrg = async () => {
    try {
      await patch('/api/settings', { name: f.name, timezone: f.timezone, route_mode: f.route_mode, retention_days: f.retention_days, lead_questions: qs.filter((q) => q.text.trim()),
        remind_rep_after_min: f.remind_rep_after_min, escalate_after_min: f.escalate_after_min, quiet_rep_after_hours: f.quiet_rep_after_hours, alerts_from_hour: f.alerts_from_hour, alerts_until_hour: f.alerts_until_hour, reps_see_all_posts: f.reps_see_all_posts })
      toast('Settings saved', 'pos'); org.reload()
    } catch (e) { toast((e as Error).message, 'neg') }
  }

  const catCols: Column<Category>[] = [
    { key: 'name', header: 'Category', cell: (r) => <><strong>{r.name}</strong> <span className="muted num">{r.key}</span></> },
    { key: 'desc', header: 'What counts (read by the screening model)', cell: (r) => <span className="muted">{r.description}</span> },
    { key: 'active', header: '', cell: (r) => r.active ? null : <Chip>Off</Chip> },
  ]
  const areaCols: Column<Area>[] = [
    { key: 'name', header: 'Area', cell: (r) => <strong>{r.name}</strong> },
    { key: 'aliases', header: 'Also written as', cell: (r) => <span className="muted">{r.aliases.join(', ')}</span> },
    { key: 'active', header: '', cell: (r) => r.active ? null : <Chip>Off</Chip> },
  ]

  return (
    <>
      <div className="tab-actions"><span className="muted">How leads are screened and routed for this company.</span><Button onClick={saveOrg}>Save company settings</Button></div>
      <div className="two-col">
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Routing and data</h2></div>
          <div className="stack-tight">
            <Field label="Organisation name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
            <FormRow label="Time zone" help="A zone name, like America/Phoenix. Days on every screen are counted in it."><input className="field" value={f.timezone} onChange={(e) => setF({ ...f, timezone: e.target.value })} /></FormRow>
            <FormRow label="Who gets a lead" help="Open question for Handpicked: every rep in the group, or only reps with a matching business.">
              <Select value={f.route_mode} onChange={(e) => setF({ ...f, route_mode: e.target.value as Org['route_mode'] })}>
                <option value="matching">Only reps with a matching subscriber</option>
                <option value="all_group_reps">Every rep in the group</option>
              </Select>
            </FormRow>
            <FormRow label="Send reps every post" help="On: posts the AI does not call a lead still go to every rep in the group, marked with the AI's read and with a phone alert, so nothing is missed. They are never sold, billed or reminded about. Replies and Not a lead taps show where the filter is wrong.">
              <Switch label="Send reps every post in their groups" checked={f.reps_see_all_posts} onChange={(e) => setF({ ...f, reps_see_all_posts: e.target.checked })} />
            </FormRow>
            <FormRow label="Keep raw emails and non-lead posts for, days" help="Posts about family care are sensitive. Leads keep their text; everything else is erased after this.">
              <input className="field" type="number" min={1} max={365} value={f.retention_days} onChange={(e) => setF({ ...f, retention_days: Number(e.target.value) })} />
            </FormRow>
          </div>
        </Panel>
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Alerts</h2><span className="muted">in the app and on phones</span></div>
          <div className="stack-tight">
            <FormRow label="Remind the rep after, minutes" help="A lead still unanswered this long after it reached the rep. The first good reply usually wins.">
              <input className="field" type="number" min={10} max={1440} value={f.remind_rep_after_min} onChange={(e) => setF({ ...f, remind_rep_after_min: Number(e.target.value) })} />
            </FormRow>
            <FormRow label="Tell admins after, minutes" help="Still unanswered this long: every admin hears about it. Must be longer than the reminder.">
              <input className="field" type="number" min={15} max={2880} value={f.escalate_after_min} onChange={(e) => setF({ ...f, escalate_after_min: Number(e.target.value) })} />
            </FormRow>
            <FormRow label="Flag a quiet rep after, hours" help="No alerts from a rep's Muse for this long usually means it stopped. Their groups go unwatched until it restarts.">
              <input className="field" type="number" min={1} max={72} value={f.quiet_rep_after_hours} onChange={(e) => setF({ ...f, quiet_rep_after_hours: Number(e.target.value) })} />
            </FormRow>
            <FormRow label="Only send alerts between" help="In the company's time zone. Anything due overnight goes out when the window opens.">
              <span className="row-tight">
                <Select aria-label="From" value={String(f.alerts_from_hour)} onChange={(e) => setF({ ...f, alerts_from_hour: Number(e.target.value) })}>
                  {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                </Select>
                <span className="muted">and</span>
                <Select aria-label="Until" value={String(f.alerts_until_hour)} onChange={(e) => setF({ ...f, alerts_until_hour: Number(e.target.value) })}>
                  {Array.from({ length: 24 }, (_, h) => h + 1).map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
                </Select>
              </span>
            </FormRow>
          </div>
        </Panel>
        <Panel>
          <div className="panel-header"><h2 className="panel-title">Lead scorecard</h2><span className="muted">answered for every lead</span></div>
          <div className="stack-tight">
            <p className="muted" style={{ margin: 0 }}>The AI answers each question yes, no or unclear from the post. Yes earns the question's full points, unclear half, no none. That gives each lead a score out of 100, and reps see their best leads first. The score ranks leads only; the tier is what bills.</p>
            {qs.map((q, i) => (
              <div key={i} className="row-tight">
                <input className="field" aria-label={`Question ${i + 1}`} value={q.text} onChange={(e) => setQs(qs.map((x, j) => j === i ? { ...x, text: e.target.value } : x))} />
                <Select aria-label={`How much question ${i + 1} counts`} value={String(q.weight || 1)} onChange={(e) => setQs(qs.map((x, j) => j === i ? { ...x, weight: Number(e.target.value) } : x))}>
                  {WEIGHTS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                </Select>
                <Button variant="ghost" onClick={() => setQs(qs.filter((_, j) => j !== i))}>Remove</Button>
              </div>
            ))}
            {qs.length < 8 && <div><Button variant="secondary" onClick={() => setQs([...qs, { key: '', text: '', weight: 1 }])}>Add question</Button></div>}
          </div>
        </Panel>
      </div>
      <div className="two-col">
        <Panel className="panel-flush">
          <div className="panel-header" style={{ padding: 'var(--s-5) var(--s-6) 0' }}><h2 className="panel-title">Categories</h2><Button variant="ghost" onClick={() => setEditCat('new')}>Add</Button></div>
          {cats.data ? <DataTable columns={catCols} rows={cats.data.categories} rowKey={(r) => r.id} onRowClick={setEditCat} /> : <LoadingBlock />}
        </Panel>
        <Panel className="panel-flush">
          <div className="panel-header" style={{ padding: 'var(--s-5) var(--s-6) 0' }}><h2 className="panel-title">Areas</h2><Button variant="ghost" onClick={() => setEditArea('new')}>Add</Button></div>
          {areas.data ? <DataTable columns={areaCols} rows={areas.data.areas} rowKey={(r) => r.id} onRowClick={setEditArea} /> : <LoadingBlock />}
        </Panel>
      </div>
      {editCat && <ItemForm kind="categories" item={editCat === 'new' ? null : editCat} onClose={() => setEditCat(null)} onSaved={() => { setEditCat(null); cats.reload() }} />}
      {editArea && <ItemForm kind="areas" item={editArea === 'new' ? null : editArea} onClose={() => setEditArea(null)} onSaved={() => { setEditArea(null); areas.reload() }} />}
    </>
  )
}

function ItemForm({ kind, item, onClose, onSaved }: { kind: 'categories' | 'areas'; item: Category | Area | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(item?.name || '')
  const [extra, setExtra] = useState(item ? ('description' in item ? item.description : item.aliases.join(', ')) : '')
  const [active, setActive] = useState(item?.active ?? true)
  const [err, setErr] = useState<string | null>(null)
  const field = kind === 'categories' ? 'description' : 'aliases'
  const save = async () => {
    const body = { name, [field]: kind === 'areas' ? extra.split(',').map((s) => s.trim()).filter(Boolean) : extra, active }
    try { item ? await patch(`/api/${kind}/${item.id}`, body) : await post(`/api/${kind}`, body); onSaved() } catch (e) { setErr((e as Error).message) }
  }
  return (
    <Modal title={item ? `Edit ${item.name}` : kind === 'categories' ? 'Add category' : 'Add area'} onClose={onClose}
           footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!name.trim()}>Save</Button></>}>
      <div className="stack-tight">
        <Field label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        {kind === 'categories'
          ? <FormRow label="What counts" help="The screening model reads this. List the words people actually use."><Textarea rows={3} value={extra} onChange={(e) => setExtra(e.target.value)} /></FormRow>
          : <FormRow label="Also written as" help="Comma separated: neighbourhoods and spellings that mean this area."><input className="field" value={extra} onChange={(e) => setExtra(e.target.value)} /></FormRow>}
        {item && <FormRow label="Status"><Select value={active ? 'on' : 'off'} onChange={(e) => setActive(e.target.value === 'on')}><option value="on">On</option><option value="off">Off</option></Select></FormRow>}
        {err && <p className="form-error">{err}</p>}
      </div>
    </Modal>
  )
}
